import { useState, useEffect } from 'react';
import { useAuth } from '../store/AuthContext';
import { useLanguage } from '../store/LanguageContext';
import {
  adminGetTestAccounts, adminImpersonate, setSessionToken, API_URL,
  type TestAccount,
} from '../lib/api';
import { UserCheck, Users, Shield, Briefcase, ChevronDown, X, Loader2, AlertCircle, Eye } from 'lucide-react';

const ROLE_META: Record<string, { label: string; icon: React.ElementType; cls: string; target: string }> = {
  superadmin: { label: 'Superadmin', icon: Shield,    cls: 'bg-red-50 text-red-700 border-red-200',          target: '/admin' },
  owner:      { label: 'Owner',      icon: Briefcase, cls: 'bg-blue-50 text-blue-700 border-blue-200',       target: '/admin' },
  reseller:   { label: 'Reseller',   icon: Users,     cls: 'bg-amber-50 text-amber-700 border-amber-200',    target: '/reseller-dashboard' },
  customer:   { label: 'Customer',   icon: UserCheck, cls: 'bg-green-50 text-green-700 border-green-200',    target: '/dashboard' },
};

/**
 * Floating widget visible ONLY for superadmin. One-click impersonation of
 * any account (owner / reseller / customer) so Franky can verify what each
 * role-tier sees without manually logging in/out.
 *
 * Sets the new session token via setSessionToken + setUser, then redirects
 * to the role-appropriate dashboard.
 */
export default function TestAccountSwitcher() {
  const { user, setUser } = useAuth();
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [accounts, setAccounts] = useState<TestAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [switching, setSwitching] = useState<string | null>(null);

  // NOTE: All hooks must run unconditionally per React's Rules of Hooks.
  // The role gate is applied AFTER all hooks below.
  // Both superadmin and owner get the switcher; owner just doesn't see
  // superadmin accounts (filtered server-side).
  const isSuperadmin = !!user && user.role === 'superadmin';
  const isOwner = !!user && user.role === 'owner';
  const canSwitch = isSuperadmin || isOwner;

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setAccounts(await adminGetTestAccounts());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  // Preload accounts so quick-switch buttons fire instantly.
  useEffect(() => {
    if (canSwitch && accounts.length === 0) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSwitch]);

  // v3.2.68 — body offset enkel voor de ReturnBanner (impersonation actief).
  // Vroeger werd 'has-admin-banner' ook geadd voor owners/superadmins die
  // niet aan het impersoneren waren → 36px lege ruimte boven de navbar.
  // Filip's klacht: 'waarom zit er hier een ruimte tussen de menu en de top'.
  // Owners gebruiken de 'Test als…' dropdown in de navbar, geen banner nodig.

  // The active switch UI lives in the Navbar dropdown for both superadmin
  // and owner. This component renders only the "return to admin" banner
  // when the user is currently impersonating someone.
  if (canSwitch) return null;
  return <ReturnBanner currentName={user?.name ?? ''} setUser={setUser} />;

  const switchTo = async (acc: TestAccount) => {
    setSwitching(acc.email);
    setError(null);
    try {
      // Snapshot the current admin token (superadmin or owner) so the
      // impersonated session can jump back without re-logging in.
      const currentToken = localStorage.getItem('gevel_session_token');
      if (currentToken && user && (user.role === 'superadmin' || user.role === 'owner')) {
        localStorage.setItem('gvs-admin-token', currentToken);
        localStorage.setItem('gvs-admin-name', user.name);
        localStorage.setItem('gvs-admin-role', user.role);
      }
      const { token, user: targetUser } = await adminImpersonate(acc.email);
      setSessionToken(token);
      setUser(targetUser);
      const target = ROLE_META[acc.role]?.target ?? '/dashboard';
      // Hard navigation so all caches/state reload cleanly under new identity
      window.location.href = target;
    } catch (e) {
      setError((e as Error).message);
      setSwitching(null);
    }
  };

  // Group accounts by role for cleaner display
  const grouped: Record<string, TestAccount[]> = {};
  for (const a of accounts) {
    (grouped[a.role] ||= []).push(a);
  }
  const roleOrder = ['superadmin', 'owner', 'reseller', 'customer', 'supplier'];

  return (
    <>
      {/* Fixed top banner — sits above the navbar (z-60 > z-50). Different
          gradient for superadmin vs owner so you instantly know which mode
          you're in. The Navbar offsets itself by injecting top padding via
          a CSS variable below. */}
      <div className={`fixed top-0 left-0 right-0 z-[10000] text-white print:hidden ${
        isSuperadmin
          ? 'bg-gradient-to-r from-red-900 via-red-800 to-amber-700'
          : 'bg-gradient-to-r from-blue-900 via-blue-800 to-teal-700'
      }`} id="test-account-switcher-banner">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-2 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-shrink-0">
            <Shield size={14} className={isSuperadmin ? 'text-amber-300' : 'text-teal-300'} />
            <span className="text-xs sm:text-sm font-bold uppercase tracking-wider">
              {isSuperadmin ? 'SUPERADMIN MODUS' : 'OWNER MODUS'}
            </span>
            <span className="hidden sm:inline text-xs opacity-80">— Test als andere rol:</span>
          </div>

          {/* Quick switch buttons.
              Skip the user's own email so they don't see "test as myself".
              Superadmin sees owners (incl. Filip). Owner sees other owners
              + resellers + customer (but never franky/superadmin). */}
          <div className="flex items-center gap-1.5 flex-wrap flex-1 min-w-0">
            {user.email !== 'filip@gevelsteenbedrijf.be' && (
              <QuickSwitchBtn label="Filip (Owner)"          email="filip@gevelsteenbedrijf.be"   icon={Briefcase} disabled={!!switching} onClick={() => quickSwitch('filip@gevelsteenbedrijf.be')} switching={switching === 'filip@gevelsteenbedrijf.be'} />
            )}
            {user.email !== 'bicek@gevelsteenbedrijf.be' && (
              <QuickSwitchBtn label="Bicek (Owner)"          email="bicek@gevelsteenbedrijf.be"   icon={Briefcase} disabled={!!switching} onClick={() => quickSwitch('bicek@gevelsteenbedrijf.be')} switching={switching === 'bicek@gevelsteenbedrijf.be'} />
            )}
            <QuickSwitchBtn label="Antwerpse Bricks"       email="bricks-antwerpen@example.com" icon={Users}     disabled={!!switching} onClick={() => quickSwitch('bricks-antwerpen@example.com')} switching={switching === 'bricks-antwerpen@example.com'} />
            <QuickSwitchBtn label="Gevel Gent"             email="gevel-gent@example.com"       icon={Users}     disabled={!!switching} onClick={() => quickSwitch('gevel-gent@example.com')} switching={switching === 'gevel-gent@example.com'} />
            <QuickSwitchBtn label="Brugse Bricks"          email="bricks-brugge@example.com"    icon={Users}     disabled={!!switching} onClick={() => quickSwitch('bricks-brugge@example.com')} switching={switching === 'bricks-brugge@example.com'} />
            <QuickSwitchBtn label="Jan (Klant B2B)"        email="jan@bouwbedrijf.be"           icon={UserCheck} disabled={!!switching} onClick={() => quickSwitch('jan@bouwbedrijf.be')} switching={switching === 'jan@bouwbedrijf.be'} />
          </div>

          <button
            onClick={() => setOpen(!open)}
            className="ml-auto flex items-center gap-1 bg-white/15 hover:bg-white/25 transition-colors rounded-md px-2.5 py-1 text-xs font-semibold"
            aria-label={t('switcher.toggle')}
          >
            <Eye size={12} /> Alle accounts <ChevronDown size={12} className={open ? 'rotate-180' : ''} />
          </button>
        </div>

        {/* Expanded list */}
        {open && (
          <div className="bg-white text-[#1a1a1a] border-t border-amber-300 shadow-lg max-h-[60vh] overflow-y-auto">
            <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-[#8a7e74]">{t('switcher.subtitle')}</p>
                <button onClick={() => setOpen(false)} className="text-[#8a7e74] hover:text-[#1a1a1a] p-1" aria-label="Close">
                  <X size={14} />
                </button>
              </div>
              {loading ? (
                <div className="py-6 text-center text-[#8a7e74] text-sm flex items-center justify-center gap-2">
                  <Loader2 size={14} className="animate-spin" /> {t('switcher.loading')}
                </div>
              ) : error ? (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 flex items-start gap-2">
                  <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold">{t('switcher.error')}</p>
                    <p className="text-red-600">{error}</p>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {roleOrder.map(role => {
                    const list = grouped[role];
                    if (!list || list.length === 0) return null;
                    const meta = ROLE_META[role] ?? { label: role, icon: UserCheck, cls: 'bg-gray-100 text-gray-700 border-gray-200', target: '/dashboard' };
                    const RoleIcon = meta.icon;
                    return list.map(acc => {
                      const isMe = acc.email === user.email;
                      const isSwitching = switching === acc.email;
                      return (
                        <button
                          key={acc.id}
                          onClick={() => !isMe && !isSwitching && switchTo(acc)}
                          disabled={isMe || isSwitching}
                          className={`text-left px-3 py-2.5 rounded-lg text-xs transition-all border flex items-center gap-3 ${
                            isMe
                              ? 'bg-gray-50 text-gray-400 cursor-not-allowed border-gray-100'
                              : 'bg-white hover:bg-[#faf7f3] border-gray-200 hover:border-[#c47a3a]/40 active:scale-[0.99]'
                          }`}
                        >
                          <span className={`flex-shrink-0 px-2 py-0.5 rounded text-[9px] font-bold uppercase border flex items-center gap-1 ${meta.cls}`}>
                            <RoleIcon size={10} />
                            {meta.label}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className={`font-semibold truncate ${isMe ? '' : 'text-[#1a1a1a]'}`}>
                              {acc.name}{isMe && <span className="ml-1 text-[10px] font-normal">(jij)</span>}
                            </p>
                            <p className="text-[10px] text-[#8a7e74] truncate">
                              {acc.email}
                              {acc.reseller_company && <span className="ml-1">· {acc.reseller_company}</span>}
                              {acc.quote_count > 0 && <span className="ml-1">· {acc.quote_count} offerte(s)</span>}
                            </p>
                          </div>
                          {isSwitching && <Loader2 size={12} className="animate-spin flex-shrink-0" />}
                        </button>
                      );
                    });
                  })}
                </div>
              )}
              <p className="text-[10px] text-[#8a7e74] mt-3 italic">{t('switcher.hint')}</p>
            </div>
          </div>
        )}
      </div>
    </>
  );

  function quickSwitch(email: string) {
    const acc = accounts.find(a => a.email === email);
    if (acc) {
      void switchTo(acc);
    } else {
      // Accounts not yet loaded — load and then switch
      void (async () => {
        try {
          const list = await adminGetTestAccounts();
          setAccounts(list);
          const found = list.find(a => a.email === email);
          if (found) void switchTo(found);
          else setError(`Account "${email}" niet gevonden`);
        } catch (e) {
          setError((e as Error).message);
        }
      })();
    }
  }
}

/**
 * Banner shown when an impersonated user (not their original admin self).
 * Provides a one-click "return to your admin" action that restores the
 * saved session token. Works for both superadmin and owner.
 */
function ReturnBanner({ currentName, setUser }: {
  currentName: string;
  setUser: (u: import('../store/AuthContext').User | null) => void;
}) {
  const [savedToken, setSavedToken] = useState<string | null>(null);
  const [savedName, setSavedName] = useState<string>('');
  const [returning, setReturning] = useState(false);

  useEffect(() => {
    if (typeof localStorage === 'undefined') return;
    setSavedToken(localStorage.getItem('gvs-admin-token'));
    setSavedName(localStorage.getItem('gvs-admin-name') ?? 'Admin');
  }, []);

  // Add body offset class while this banner is rendered.
  useEffect(() => {
    if (savedToken) {
      document.body.classList.add('has-admin-banner');
      return () => document.body.classList.remove('has-admin-banner');
    }
  }, [savedToken]);

  if (!savedToken) return null;

  const goBack = async () => {
    setReturning(true);
    try {
      // Fix Franky (19/8, gsm 'blijft hangen'): controleer eerst of de
      // bewaarde admin-sessie nog geldig is. Geldig -> direct terug naar de
      // admin. Verlopen -> netjes uitleggen en schoon opnieuw laten inloggen
      // in plaats van te stranden op een dood token.
      const r = await fetch(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${savedToken}` },
      });
      localStorage.removeItem('gvs-admin-token');
      localStorage.removeItem('gvs-admin-name');
      localStorage.removeItem('gvs-admin-role');
      if (r.ok) {
        setSessionToken(savedToken);
        setUser(null);
        window.location.replace('/admin');
      } else {
        localStorage.removeItem('gevel_session_token');
        setUser(null);
        alert(`De vorige sessie van ${savedName} is intussen verlopen. Log gewoon opnieuw in (${savedName.includes('Filip') ? 'filip@gevelsteenbedrijf.be + code 123456' : 'met je eigen gegevens'}).`);
        window.location.replace('/');
      }
    } catch {
      setReturning(false);
      alert('Geen verbinding — probeer nog eens.');
    }
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-[10000] bg-gradient-to-r from-amber-500 via-amber-600 to-orange-600 text-white print:hidden" id="test-account-switcher-banner">
      {/* Teruggedraaid (Franky 19/8): deze balk staat weer op ALLE schermen —
           zonder raakte hij op gsm niet meer uit het reseller-account. */}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-2 flex items-center gap-3 flex-wrap">
        <Eye size={14} className="text-white/90 flex-shrink-0" />
        <span className="text-xs sm:text-sm font-bold flex-1 min-w-0 truncate">
          🟡 Test-modus — je bekijkt deze site als <strong>{currentName}</strong>
        </span>
        <button
          onClick={goBack}
          disabled={returning}
          className="flex items-center gap-1.5 bg-white text-orange-700 hover:bg-white/90 transition-colors rounded-md px-3 py-1 text-xs font-bold disabled:opacity-50"
        >
          {returning ? <Loader2 size={11} className="animate-spin" /> : <Shield size={11} />}
          Terug naar {savedName}
        </button>
      </div>
    </div>
  );
}

function QuickSwitchBtn({ label, icon: Icon, disabled, onClick, switching }: {
  label: string;
  email: string;
  icon: React.ElementType;
  disabled: boolean;
  onClick: () => void;
  switching: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="flex items-center gap-1.5 bg-white/15 hover:bg-white text-white hover:text-[#1a1a1a] transition-all rounded-md px-2.5 py-1 text-[11px] font-semibold whitespace-nowrap disabled:opacity-50 disabled:cursor-wait"
    >
      {switching ? <Loader2 size={11} className="animate-spin" /> : <Icon size={11} />}
      {label}
    </button>
  );
}
